// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useState } from 'react';

import type { AutodeskUrn } from '@evolvemep/api-ts';
import { Button, Flex } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import {
  type ForgeViewerSelection,
  MULTI_SELECT_TOOL_NAME,
  WINDOW_SELECT_TOOL_NAME,
} from 'components/Autodesk/extensions/SelectionToolsExtension/types';
import type { SpoolingSettings } from 'components/Autodesk/extensions/SpoolsExtension/types';
import { useAutodeskExtensionStoreSnapshot } from 'components/Autodesk/useAutodeskExtensionStore';
import type { DeepReadonly } from 'helpers/helper-types';
import { useRequestModeIsolation } from 'modules/Shop/Spooling/SpoolRequests/useRequestModeIsolation';

import { StartRequestPopover } from './StartRequestPopover';

type Props = {
  spoolingSettings: SpoolingSettings;
  onClose: () => void;
  urn: AutodeskUrn;
};

export const RequestNewSpools = ({ spoolingSettings, onClose, urn }: Props) => {
  const { commands, selection } = useAutodeskExtensionStoreSnapshot('apsViewer');
  const hasSelection = selection.length > 0;

  const isolateForRequestMode = useRequestModeIsolation(spoolingSettings);

  useEffect(() => {
    commands?.setActiveNavigationTool(WINDOW_SELECT_TOOL_NAME);
  }, [commands]);

  const [focusedElements, setFocusedElements] = useState<DeepReadonly<ForgeViewerSelection[]> | null>(null);

  const requestStarted = focusedElements !== null;

  useEffect(() => {
    isolateForRequestMode(focusedElements?.map(({ dbId }) => dbId) ?? null, true);
  }, [isolateForRequestMode, focusedElements]);

  if (requestStarted) {
    return (
      <StartRequestPopover
        selection={selection}
        onEndSelection={() => {
          setFocusedElements(null);
          commands?.setActiveNavigationTool(WINDOW_SELECT_TOOL_NAME);
        }}
        urn={urn}
      />
    );
  }

  return (
    <Flex gap="xs">
      <Button
        variant="outline"
        onClick={() => {
          commands?.setActiveNavigationTool(WINDOW_SELECT_TOOL_NAME);
          isolateForRequestMode(null, false);
          onClose();
        }}
        leftSection={<EvolveIcon icon="Exit" />}
      >
        Exit
      </Button>
      <Button
        disabled={!hasSelection}
        onClick={() => {
          if (selection.length === 0) {
            notifications.show({
              title: 'No objects selected',
              message: "Select objects using the model browser's selection tools before adding them as spool.",
              color: 'red',
            });
            return;
          }
          setFocusedElements(selection);
          commands?.setActiveNavigationTool(MULTI_SELECT_TOOL_NAME);
        }}
      >
        Add selected as spool
      </Button>
    </Flex>
  );
};
